import { useMemo, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, Layers, KeyRound, Users } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { InfoTip } from "../components/Erklaerung";
import { Badge, Card, KlasseBadge } from "../components/ui";
import { RoleName } from "../components/RoleName";
import { useApp } from "../../store/AppStore";
import { roleStats, roleHolders, roleSystems, unassignedRoles, rolesWithoutLicenseEffect, simulateAssign, simulateRevoke, type SimResult } from "../../domain/analysis";
import { ruleHint } from "../../domain/ruleHints";
import { classifyValue } from "../../domain/classification";
import { formatNum, formatFue, formatEuroMio, formatWeightExact, CLASS_LABEL, type RatioTable } from "../../domain/rules";
import { isFueRelevantSid } from "../../data/licensing";
import type { Dataset } from "../../data/generator";
import type { LicenseClass, Person } from "../../domain/types";

const ROW_H = 40;
const VIEWPORT_H = 420;

type Filter = "alle" | "ohne-traeger" | "ohne-wirkung";

/** Badge für Rollen auf Systemen ohne FUE-Metrik (GSP, HCM) — Befund C3: dort gibt es keine FUE-Klasse. */
function KeineFueWirkung() {
  return <Badge tone="outline">keine FUE-Wirkung · eigene Metrik</Badge>;
}

export function Rollen() {
  const { ds } = useApp();
  const [params, setParams] = useSearchParams();
  const sel = params.get("rolle");
  const filter = (params.get("filter") as Filter | null) ?? "alle";
  const stats = useMemo(() => roleStats(ds), [ds]);
  const ohneTraeger = useMemo(() => unassignedRoles(ds), [ds]);
  const ohneWirkung = useMemo(() => rolesWithoutLicenseEffect(ds), [ds]);

  const select = (id: string) => { const n = new URLSearchParams(params); n.set("rolle", id); setParams(n, { replace: true }); };
  const back = () => { const n = new URLSearchParams(params); n.delete("rolle"); setParams(n, { replace: true }); };
  const setFilter = (f: Filter) => { const n = new URLSearchParams(params); if (f === "alle") n.delete("filter"); else n.set("filter", f); n.delete("rolle"); setParams(n, { replace: true }); };

  const single = sel ? ds.singleRoles.find((s) => s.id === sel) : undefined;
  const composite = sel ? ds.compositeRoles.find((c) => c.id === sel) : undefined;

  return (
    <div>
      <PageHeader route="/rollen" />
      {sel && (single || composite) ? (
        <RoleDetail id={sel} onBack={back} onSelect={select} />
      ) : (
        <RoleList stats={stats} onSelect={select} />
      )}
    </div>
  );

  function RoleList({ stats, onSelect }: { stats: Map<string, { holders: number; systems: number }>; onSelect: (id: string) => void }) {
    const ohneTraegerIds = useMemo(() => new Set(ohneTraeger.map((r) => r.id)), [ohneTraeger]);
    const ohneWirkungIds = useMemo(() => new Set(ohneWirkung.map((r) => r.id)), [ohneWirkung]);
    const rows = [
      ...ds.compositeRoles.map((c) => ({ id: c.id, name: c.name, kind: "Sammelrolle" as const, landscapeId: c.landscapeId, sid: c.sid, cls: null as LicenseClass | null, ...(stats.get(c.id) ?? { holders: 0, systems: 0 }) })),
      ...ds.singleRoles.map((s) => ({ id: s.id, name: s.name, kind: "Einzelrolle" as const, landscapeId: s.landscapeId, sid: s.sid, cls: s.forcedClass, ...(stats.get(s.id) ?? { holders: 0, systems: 0 }) })),
    ]
      .filter((r) => filter === "alle" || (filter === "ohne-traeger" ? ohneTraegerIds.has(r.id) : ohneWirkungIds.has(r.id)))
      .sort((a, b) => b.holders - a.holders || a.id.localeCompare(b.id));
    const kachel = (f: Filter, label: string, n: number, tip: ReactNode) => (
      <button type="button" onClick={() => setFilter(filter === f ? "alle" : f)} aria-pressed={filter === f}
        className="bg-card shadow-card flex flex-col rounded-xl border-soft px-5 py-4 text-left hover:bg-sunken" style={{ borderWidth: 1, borderColor: filter === f ? "var(--color-brand-600)" : undefined }}>
        <div className="text-dim inline-flex items-center gap-1 text-[13px] font-medium tracking-wide uppercase">{label} {tip}</div>
        <div className="tnum text-strong mt-1.5 text-2xl font-semibold">{formatNum(n)}</div>
        <div className="text-faint mt-1 text-xs">{filter === f ? "Filter aktiv · Klick hebt auf" : "Klick filtert die Liste"}</div>
      </button>
    );
    return (
      <div>
        <div className="mb-6 grid gap-4 sm:grid-cols-2">
          {kachel("ohne-traeger", "Rollen ohne Träger", ohneTraeger.length, <InfoTip>Einzel- und Sammelrollen, die keinem Konto zugewiesen sind. Sie erzeugen keine Lizenzwirkung, aber Pflegeaufwand und Prüfungsrisiko. Empfehlung: löschen oder archivieren — RFP 2.2 „unassigned roles". Die Zahl kommt aus der Rollenzuordnung (rollenzuordnung.csv), nicht aus einem Kennzeichen.</InfoTip>)}
          {kachel("ohne-wirkung", "Rollen ohne Lizenzwirkung", ohneWirkung.length, <InfoTip>Rollen mit Trägern, deren Berechtigungsobjekte im PCE-Regelwerk keine Regel auslösen — sie tragen keine Klasse. Eine Sammelrolle zählt, wenn keine ihrer Einzelrollen einen Regeltreffer hat. Träger dieser Rollen sind über die Untergrenze trotzdem mindestens Self-Service, solange sie ein Dialogkonto auf einem FUE-System haben.</InfoTip>)}
        </div>
        <Card title="Alle Rollen" subtitle={filter === "alle" ? "Einzel- und Sammelrollen · Klick öffnet die Rolle" : filter === "ohne-traeger" ? `${formatNum(rows.length)} Rollen ohne Träger · Klick öffnet die Rolle` : `${formatNum(rows.length)} Rollen ohne Lizenzwirkung · Klick öffnet die Rolle`}
          right={filter !== "alle" ? <button type="button" onClick={() => setFilter("alle")} className="text-xs font-medium hover:underline" style={{ color: "var(--color-brand-600)" }}>Filter aufheben</button> : undefined}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-soft text-faint text-xs uppercase tracking-wide">
                  <th className="py-2 text-left">Rolle</th><th className="py-2 text-left">Art</th><th className="py-2 text-left">System</th>
                  <th className="py-2 text-left">Klasse</th><th className="py-2 text-right">Träger</th><th className="py-2 text-right">Systeme</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => onSelect(r.id)} className="hover:bg-sunken cursor-pointer border-b border-soft last:border-0">
                    <td className="py-2.5">
                      <span className="inline-flex flex-wrap items-center gap-1.5">{r.kind === "Sammelrolle" ? <Layers size={14} className="text-faint shrink-0" /> : <KeyRound size={14} className="text-faint shrink-0" />}<RoleName id={r.id} name={r.name} />{r.holders === 0 && <Badge tone="amber">ohne Träger</Badge>}{r.holders > 0 && ohneWirkungIds.has(r.id) && <Badge tone="gray">ohne Lizenzwirkung</Badge>}</span>
                    </td>
                    <td className="text-dim py-2.5">{r.kind}</td>
                    <td className="text-dim py-2.5"><span className="font-mono">{r.sid}</span> <span className="text-faint text-xs">({r.landscapeId})</span></td>
                    <td className="py-2.5">{!isFueRelevantSid(r.sid) ? <KeineFueWirkung /> : r.cls && r.cls !== "NotRelevant" ? <KlasseBadge cls={r.cls} /> : r.cls === "NotRelevant" ? <span className="text-faint text-xs">keine Lizenzwirkung</span> : <span className="text-faint text-xs">—</span>}</td>
                    <td className="tnum text-normal py-2.5 text-right font-medium">{formatNum(r.holders)}</td>
                    <td className="tnum text-dim py-2.5 text-right">{formatNum(r.systems)}</td>
                    <td className="py-2.5 text-right"><ChevronRight size={15} className="text-faint" /></td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={7} className="text-dim py-4 text-center text-sm">Keine Rolle in dieser Auswahl.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    );
  }

  function RoleDetail({ id, onBack, onSelect }: { id: string; onBack: () => void; onSelect: (id: string) => void }) {
    const sr = ds.singleRoles.find((s) => s.id === id);
    const c = ds.compositeRoles.find((x) => x.id === id);
    const sid = sr?.sid ?? c?.sid ?? "";
    const fue = isFueRelevantSid(sid);
    const systems = useMemo(() => roleSystems(id, ds), [id]);
    const holders = useMemo(() => roleHolders(id, ds), [id]);
    const [scrollTop, setScrollTop] = useState(0);
    const start = Math.max(0, Math.floor(scrollTop / ROW_H) - 6);
    const end = Math.min(holders.length, Math.ceil((scrollTop + VIEWPORT_H) / ROW_H) + 6);
    // Technischer Name zuerst, Kurztext daneben — fehlt der Kurztext, steht nur der Name.
    const name = sr?.name ?? c?.name ?? "";
    const klasseBadge = (cls: LicenseClass) => !fue ? <KeineFueWirkung /> : cls === "NotRelevant" ? <Badge tone="gray">keine Lizenzwirkung</Badge> : <KlasseBadge cls={cls} />;

    return (
      <div>
        <button onClick={onBack} className="text-dim hover:text-strong mb-3 inline-flex items-center gap-1.5 text-sm"><ArrowLeft size={15} /> Alle Rollen</button>

        {c && (
          <Card title={<span className="inline-flex flex-wrap items-baseline gap-x-2">Sammelrolle <RoleName id={id} name={name} />{holders.length === 0 && <Badge tone="amber">ohne Träger</Badge>}</span>} subtitle="bündelt Einzelrollen · keine eigenen Berechtigungswerte" className="mb-6">
            <div className="space-y-2">
              {c.singleRoleIds.map((sid) => { const s = ds.singleRoles.find((x) => x.id === sid); if (!s) return null; return (
                <button key={sid} onClick={() => onSelect(sid)} className="hover:bg-sunken flex w-full items-center justify-between gap-3 rounded-lg border-soft p-3 text-left" style={{ borderWidth: 1 }}>
                  <span className="text-sm"><RoleName id={s.id} name={s.name} /></span>
                  <span className="flex items-center gap-2">{klasseBadge(s.forcedClass)}{s.removable && <Badge tone="gray">vermeidbar</Badge>}<ChevronRight size={15} className="text-faint" /></span>
                </button>
              ); })}
            </div>
          </Card>
        )}

        {sr && (
          <Card title={<span className="inline-flex flex-wrap items-baseline gap-x-2">Einzelrolle <RoleName id={id} name={name} />{holders.length === 0 && <Badge tone="amber">ohne Träger</Badge>}</span>} subtitle={<span className="inline-flex items-center gap-1">{`System ${sr.sid} · ${sr.authValues.length} von ${sr.objectsTotal} Objekten lizenzrelevant`} <InfoTip>Gezeigt werden nur Objekte, die im PCE-Regelwerk eine Regel auslösen. Die übrigen {sr.objectsTotal - sr.authValues.length} Objekte dieser Rolle haben keine Lizenzwirkung.{!fue && <> Das System {sr.sid} wird nicht über FUE vermessen (eigene Metrik) — die Rolle trägt dort keine FUE-Klasse.</>}</InfoTip></span>} className="mb-6"
            right={<span className="flex items-center gap-2">{klasseBadge(sr.forcedClass)}{sr.removable && <Badge tone="gray">vermeidbar</Badge>}</span>}>
            {sr.authValues.map((av) => ruleHint(av)).filter(Boolean).length > 0 && (
              <div className="mb-3 rounded-lg p-3 text-xs" style={{ borderWidth: 1, borderColor: "var(--color-amp-amber)", backgroundColor: "var(--surface-sunken)" }}>
                <div className="text-normal mb-1 font-medium">Warum diese Einstufung? — Hinweise aus dem Regelwerk</div>
                <ul className="text-dim space-y-1">
                  {sr.authValues.map((av) => { const h = ruleHint(av); return h ? <li key={`${av.obj}-${av.val}`}><span className="font-mono">{av.obj} {av.field} = {av.val}</span>: {h}</li> : null; })}
                </ul>
              </div>
            )}
            {sr.authValues.length === 0 ? <div className="text-dim text-sm">Kein Objekt dieser Rolle löst eine Regel im PCE-Regelwerk aus — keine Lizenzwirkung.</div> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-soft text-faint text-xs uppercase tracking-wide"><th className="py-2 text-left">Objekt · Feld · Wert</th><th className="py-2 text-left">Regel (Step)</th><th className="py-2 text-left">Klasse</th></tr></thead>
                  <tbody>
                    {sr.authValues.map((av, i) => { const rule = classifyValue(av); return (
                      <tr key={i} className="border-b border-soft last:border-0">
                        <td className="text-strong py-2 font-mono text-[13px]">{av.obj} {av.field} = {av.val}</td>
                        <td className="text-dim py-2">{rule ? `Step ${rule.step}` : "—"}{rule?.ruleClass ? ` · ${rule.ruleClass}` : ""}</td>
                        <td className="py-2">{rule?.cls ? <KlasseBadge cls={rule.cls} /> : <span className="text-faint text-xs">Engine/technisch</span>}</td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {holders.length === 0 && (
          <Card title="Rolle ohne Träger" className="mb-6" subtitle={<span className="inline-flex items-center gap-1">Keinem Konto zugewiesen <InfoTip>Empfehlung: löschen oder archivieren — RFP 2.2 „unassigned roles". Eine nicht vergebene Rolle erzeugt keine Lizenzwirkung, aber Pflegeaufwand und Prüfungsrisiko; wird sie später doch vergeben, gilt die hier gezeigte Klasse.</InfoTip></span>}>
            <div className="flex flex-wrap gap-2 text-sm"><Badge tone="amber">ohne Träger</Badge>{sr && klasseBadge(sr.forcedClass)}</div>
          </Card>
        )}

        <Card title="In welchen Systemen vergeben" className="mb-6">
          <div className="flex flex-wrap gap-2">
            {systems.length === 0 ? <span className="text-dim text-sm">In keinem System vergeben.</span> : systems.map((s) => (
              <span key={s.sid} className="bg-sunken inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm"><span className="text-strong font-mono text-xs">{s.sid}</span><span className="tnum text-dim">{formatNum(s.persons)}</span></span>
            ))}
          </div>
        </Card>

        <Vorpruefung roleId={id} ds={ds} holders={holders} />

        <Card
          title="Träger dieser Rolle"
          subtitle={<span className="inline-flex items-center gap-1">{sr ? `${formatNum(holders.length)} Personen · Klasse durch diese Rolle: ${fue ? CLASS_LABEL[sr.forcedClass] : "keine FUE-Wirkung"} (für alle gleich)` : `${formatNum(holders.length)} Personen`} <InfoTip>Eine Rolle hat auf einem System dieselben Berechtigungswerte und löst deshalb bei jedem Träger dieselbe Klasse aus. Die Klasse der Person ist das Maximum über alle ihre Rollen, Systeme und Mandanten (R2) und kann deshalb höher sein — die Zeile nennt dann die Rolle und das System, die sie hochziehen.</InfoTip></span>}
        >
          {holders.length === 0 ? <div className="text-dim text-sm">Keine Träger.</div> : (
            <div onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)} style={{ height: VIEWPORT_H, overflowY: "auto" }}>
              <div style={{ height: start * ROW_H }} />
              {holders.slice(start, end).map((p) => (
                <Link key={p.id} to={`/berechtigungen?person=${p.id}`} className="hover:bg-sunken flex items-center gap-3 border-b border-soft px-2" style={{ height: ROW_H }}>
                  <Users size={13} className="text-faint shrink-0" />
                  <span className="font-mono text-xs font-medium" style={{ color: "var(--color-brand-600)" }}>{p.id}</span>
                  <span className="text-faint text-[11px]">Person</span>
                  <KlasseBadge cls={p.licenseClass} />
                  {(() => {
                    // Person höher als diese Rolle? Dann zieht eine andere Rolle hoch — die steht hier.
                    const top = p.drivers.reduce<typeof p.drivers[number] | undefined>((b, d) => (!b || (d.ruleStep ?? 9999) < (b.ruleStep ?? 9999) ? d : b), undefined);
                    if (!sr || !top || top.roleId === id || (top.ruleStep ?? 9999) >= (sr.forcedStep ?? 9999)) return null;
                    return <span className="text-faint truncate text-xs">durch <span className="font-mono">{top.roleId}</span> auf {top.sid}</span>;
                  })()}
                  <span className="text-faint ml-auto text-xs">{p.drivingSystem}</span>
                </Link>
              ))}
              <div style={{ height: Math.max(0, (holders.length - end) * ROW_H) }} />
            </div>
          )}
        </Card>
      </div>
    );
  }
}

// ------------------------------------------------------------ Vorprüfung: Rolle vergeben / entziehen
type ZielArt = "typ" | "einheit" | "landschaft" | "abteilung" | "ids" | "rolle";

function Vorpruefung({ roleId, ds, holders }: { roleId: string; ds: Dataset; holders: Person[] }) {
  const { ratio, price } = useApp();
  const [art, setArt] = useState<ZielArt>("abteilung");
  const [wert, setWert] = useState("");
  const [ids, setIds] = useState("");
  const [modus, setModus] = useState<"vergabe" | "entzug">("vergabe");
  const [n, setN] = useState(100);

  const optionen = useMemo(() => {
    const set = (xs: (string | undefined)[]) => [...new Set(xs.filter((x): x is string => !!x))].sort();
    return {
      typ: set(ds.persons.map((p) => p.type)),
      einheit: set(ds.persons.map((p) => p.unit)),
      landschaft: set(ds.persons.flatMap((p) => p.landscapeIds)),
      abteilung: set(ds.persons.map((p) => p.department)),
      rolle: [...ds.compositeRoles.map((c) => c.id), ...ds.singleRoles.map((s) => s.id)].filter((x) => x !== roleId).sort(),
    };
  }, [ds, roleId]);

  const zielIds = useMemo((): string[] => {
    if (modus === "entzug") return [];
    if (art === "ids") return ids.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
    if (!wert) return [];
    if (art === "rolle") return roleHolders(wert, ds).map((p) => p.id);
    const f: (p: Person) => boolean = art === "typ" ? (p) => p.type === wert : art === "einheit" ? (p) => p.unit === wert : art === "landschaft" ? (p) => p.landscapeIds.includes(wert) : (p) => p.department === wert;
    return ds.persons.filter(f).map((p) => p.id);
  }, [modus, art, wert, ids, ds]);

  const result: SimResult | null = useMemo(() => {
    if (modus === "entzug") return simulateRevoke(roleId, ds, ratio);
    if (zielIds.length === 0) return null;
    return simulateAssign(roleId, zielIds, ds, ratio);
  }, [modus, roleId, zielIds, ds, ratio]);

  const sel = "bg-card rounded-md border-soft px-2 py-1 text-sm" as const;
  return (
    <Card title="Vorprüfung: Was passiert, wenn diese Rolle vergeben wird?" className="mb-6"
      subtitle={<span className="inline-flex items-center gap-1">Klasse vorher → nachher je Person, Δ FUE und Δ EUR / Jahr <InfoTip>Vergabe: Klasse nachher = Maximum über die bestehenden Rollen der Person und diese Rolle (R2), Untergrenze Self-Service. Entzug: Klasse ohne diese Rolle über alle Systeme und Mandanten (classWithout). Δ EUR = Δ FUE × Preis je FUE aus den Einstellungen. Rollen auf Systemen ohne FUE-Metrik ändern keine Klasse.</InfoTip></span>}>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
        <select className={sel} style={{ borderWidth: 1 }} value={modus} onChange={(e) => { setModus(e.target.value as "vergabe" | "entzug"); setN(100); }} aria-label="Modus">
          <option value="vergabe">Vergabe simulieren</option>
          <option value="entzug">Entzug simulieren ({formatNum(holders.length)} Träger)</option>
        </select>
        {modus === "vergabe" && (
          <>
            <select className={sel} style={{ borderWidth: 1 }} value={art} onChange={(e) => { setArt(e.target.value as ZielArt); setWert(""); setN(100); }} aria-label="Zielpersonen">
              <option value="typ">Typ</option><option value="einheit">Einheit</option><option value="landschaft">Landschaft</option><option value="abteilung">Abteilung</option><option value="rolle">alle Träger einer anderen Rolle</option><option value="ids">Personen-IDs</option>
            </select>
            {art === "ids" ? (
              <input className={sel} style={{ borderWidth: 1, minWidth: 260 }} value={ids} onChange={(e) => { setIds(e.target.value); setN(100); }} placeholder="P-10000001, P-10000002 …" aria-label="Personen-IDs" />
            ) : (
              <select className={sel} style={{ borderWidth: 1, maxWidth: 320 }} value={wert} onChange={(e) => { setWert(e.target.value); setN(100); }} aria-label="Wert">
                <option value="">— wählen —</option>
                {optionen[art].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            <span className="text-faint text-xs">{formatNum(zielIds.length)} Zielpersonen</span>
          </>
        )}
      </div>
      {!result ? <div className="text-dim text-sm">Zielpersonen wählen — die Rechnung erscheint sofort.</div> : <SimErgebnis r={result} ratio={ratio} price={price} modus={modus} n={n} onMore={() => setN((x) => x + 200)} />}
    </Card>
  );
}

function SimErgebnis({ r, price, modus, n, onMore }: { r: SimResult; ratio: RatioTable; price: number; modus: "vergabe" | "entzug"; n: number; onMore: () => void }) {
  const eur = r.deltaFue * price;
  const vorz = modus === "vergabe" ? "+" : "−";
  const Kachel = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
    <div className="bg-sunken rounded-lg px-4 py-3"><div className="text-faint text-[11px] uppercase tracking-wide">{label}</div><div className="tnum text-strong mt-1 text-xl font-semibold">{value}</div>{sub && <div className="text-faint text-xs">{sub}</div>}</div>
  );
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs"><span className="text-dim">Klasse durch die Rolle:</span>{r.roleClass ? <KlasseBadge cls={r.roleClass} /> : <Badge tone="gray">keine Lizenzwirkung</Badge>}<span className="text-faint">auf <span className="font-mono">{r.sid}</span></span></div>
      <div className="mb-3 grid gap-3 sm:grid-cols-4">
        <Kachel label="Personen geprüft" value={formatNum(r.persons)} />
        <Kachel label="mit Klassenwechsel" value={formatNum(r.changed)} sub={r.persons ? `${Math.round((r.changed / r.persons) * 100)} %` : undefined} />
        <Kachel label="Δ FUE" value={`${vorz}${formatFue(r.deltaFue)}`} />
        <Kachel label="Δ EUR / Jahr" value={`${vorz}${formatEuroMio(eur)}`} sub={`× ${formatNum(price)} EUR je FUE`} />
      </div>
      {r.transitions.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5 text-xs">
          {r.transitions.map((t) => <span key={`${t.fromClass}>${t.toClass}`} className="bg-sunken inline-flex items-center gap-1 rounded px-2 py-0.5"><span className="tnum text-strong font-semibold">{formatNum(t.persons)} ×</span> {CLASS_LABEL[t.fromClass]} → {CLASS_LABEL[t.toClass]} <span className="tnum text-dim">({vorz}{formatFue(t.fue)})</span></span>)}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-faint text-left uppercase tracking-wide"><th className="px-2 py-1 font-medium">Person</th><th className="px-2 py-1 font-medium">Name</th><th className="px-2 py-1 font-medium">Abteilung</th><th className="px-2 py-1 font-medium">Klasse</th><th className="px-2 py-1 text-right font-medium">Δ FUE</th></tr></thead>
          <tbody>
            {r.rows.slice(0, n).map((row) => (
              <tr key={row.id} style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
                <td className="px-2 py-1 font-mono whitespace-nowrap"><Link to={`/berechtigungen?person=${row.id}`} className="hover:underline" style={{ color: "var(--color-brand-600)" }}>{row.id}</Link></td>
                <td className="text-strong px-2 py-1">{row.name ?? "—"}</td>
                <td className="text-dim px-2 py-1">{row.department ?? "—"}</td>
                <td className="px-2 py-1"><KlasseBadge cls={row.from} /> → <KlasseBadge cls={row.to} outline={row.to === row.from} /></td>
                <td className="tnum px-2 py-1 text-right" style={{ color: row.delta > 0 ? (modus === "vergabe" ? "var(--color-amp-amber)" : "var(--color-amp-green)") : undefined }}>{row.delta > 0 ? `${vorz}${formatWeightExact(row.delta)}` : "0"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {r.rows.length > n && <div className="mt-1 flex items-center gap-3 text-xs"><span className="text-faint">{formatNum(n)} von {formatNum(r.rows.length)} gezeigt</span><button type="button" onClick={onMore} className="font-medium hover:underline" style={{ color: "var(--color-brand-600)" }}>weitere 200</button></div>}
      </div>
    </div>
  );
}
